import { mat4 } from 'gl-matrix';
import { Grid } from '~/engine/data/common/Grid';
import { assembleVolumeFromSlices } from '~/domain/services/assembleVolume';
import { resampleInt16Grid } from '~/domain/services/resampleGrid';
import { buildDerivedSeriesFromVolume } from '~/domain/services/dicomRewriteImage';
import { ReformatUnsupportedError } from '~/domain/errors/DomainError';
import { ResampleSize } from './reformatValidation';
import { gridBoundsMm } from './gridGeometry';
import { sourcePaddingValue } from './paddingValue';
import { paddedFractionInBox } from './orientedBox';
import { reformatDerivationDescription } from './derivationDescription';
import {
    NO_EXTENSION,
    ReformatExtension,
    ReformatRegion,
    buildExtendedReformatGrid,
    buildReformatGrid,
    defaultRegionForSlices,
    excludedByExtensionMm,
    extendedMarginMm,
} from './reformatRegion';
import { reformatRegionRefusal } from './reformatEligibility';
import { isWritableSeriesNumber } from './seriesNumbering';
import { DicomVRMaxLength } from '~/domain/dicom/dicomConstants';
import { dicomStringViolation } from '~/domain/dicom/valueRepresentation';
import { replicateBoundarySlices } from './sliceReplication';
import {
    ReformatTarget,
    reformatSourceBoxMm,
    reformatSourceSlices,
    reformatTargetModality,
} from './reformatTarget';

export type { ReformatTarget };

export interface ReformatParams {
    target: ReformatTarget;
    /** The part of the patient to write out. See {@link ReformatRegion}. */
    region: ReformatRegion;
    /**
     * Optional replication of the boundary slice beyond the region, for
     * photon scatter. See {@link ReformatExtension}.
     */
    extension?: ReformatExtension;
    sliceThicknessMm: number;
    resampleSize: ResampleSize;
    outputDescription: string;
    /** `SeriesNumber` for the output. See {@link nextFreeSeriesNumber}. */
    seriesNumber: number;
    /**
     * Numbers already in use.
     *
     * Checked HERE and not only where the user picks one: a raw series can
     * arrive between the dialog's last render and the start, and any other
     * entry point would not check at all. A duplicate is the single thing
     * `nextFreeSeriesNumber` exists to avoid, so it is refused wherever the
     * number is actually written.
     */
    usedSeriesNumbers: ReadonlySet<number>;
}

export interface ReformatResult {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dicomDatasets: any[];
}

/**
 * Execute the reformat workflow: assemble/read source volume,
 * build destination grid, resample, and generate derived DICOM datasets.
 *
 * The caller handles persistence (DicomProcessor) and UI feedback.
 */
export async function executeReformat(
    params: ReformatParams
): Promise<ReformatResult> {
    const {
        target,
        region,
        extension = NO_EXTENSION,
        sliceThicknessMm,
        resampleSize,
        outputDescription,
        seriesNumber,
        usedSeriesNumbers,
    } = params;

    // Refuse before reading any pixels: the accessors below throw for a
    // blocked target, and a stated reason is worth more to the user than
    // a reformat that starts and then fails. Same predicate the dialog
    // gates its start control on, so the two cannot disagree.
    const refusal = reformatRegionRefusal(
        target,
        region,
        resampleSize,
        sliceThicknessMm,
        extension
    );
    if (refusal !== null) {
        throw new ReformatUnsupportedError(refusal);
    }
    if (usedSeriesNumbers.has(seriesNumber)) {
        throw new ReformatUnsupportedError('seriesNumberTaken');
    }
    // The user can type any number into the field; only the PROPOSED one
    // came from `nextFreeSeriesNumber`, which is where the range check
    // lived. An unencodable number reaches PixelData either way.
    if (!isWritableSeriesNumber(seriesNumber)) {
        throw new ReformatUnsupportedError('seriesNumberOutOfRange');
    }
    // SeriesDescription is VR LO. A value the panel would not accept can
    // still arrive from another caller, and an unencodable one reaches the
    // exported object either way.
    if (
        dicomStringViolation(outputDescription, DicomVRMaxLength.LONG, {
            allowEmpty: true,
        }) !== null
    ) {
        throw new ReformatUnsupportedError('seriesDescriptionInvalid');
    }

    let srcPixels: Int16Array;
    let srcGrid: Grid;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let exampleDataset: any;

    let gapFilledMm = 0;
    if (target.kind === 'rawSeries') {
        // The whole series is assembled, not the selected range: the
        // destination grid already limits what is written, and dropping
        // slices first only risks removing one the outermost output slice
        // interpolates from. It also kept the two target shapes from
        // producing the same pixels for the same region.
        const assembled = assembleVolumeFromSlices(target.series);
        srcPixels = assembled.pixels;
        srcGrid = assembled.grid;
        gapFilledMm = assembled.gapFilledMm;
        exampleDataset =
            target.series.dicomImageSlices[0].datasetWrapper.dataset;
    } else {
        srcPixels = target.image.pixelData;
        srcGrid = target.image.grid;
        exampleDataset = target.image.firstSlice.datasetWrapper.dataset;
        // A loaded image reaches the same place by a different route: a
        // series with slices missing at whole multiples of its spacing is
        // loaded WITH those slices interpolated, so it arrives here as an
        // ordinary image whose gaps have already been filled. Undeclared,
        // the same acquisition would carry a duplication clause when
        // reformatted raw and none when reformatted loaded.
        gapFilledMm =
            target.image.interpolatedSliceCount * target.image.grid.resZ;
    }

    // Reformat is MPR transversal: the region alone fixes the output geometry,
    // for either target shape and whatever the source orientation. This is
    // the same grid the dialog reads its figures from.
    const output = buildExtendedReformatGrid(
        region,
        resampleSize,
        sliceThicknessMm,
        extension
    );
    const dstGrid = output.grid;
    const replicatedSlices = output.inferiorSlices + output.superiorSlices;

    // Padding is measured on the SELECTED region against the source's own
    // box — the same box the dialog measures against. The extension's
    // slices are replicas, not padding, so they are excluded from the
    // proportion rather than counted as fill.
    const paddedOfRegion = paddedFractionInBox(
        gridBoundsMm(buildReformatGrid(region, resampleSize, sliceThicknessMm)),
        reformatSourceBoxMm(target)
    );

    // Samples past the source are padded with the source's own minimum
    // rather than edge-clamped, so the field of view can reach beyond the
    // acquisition without smearing its border outward.
    const paddingValue = sourcePaddingValue(
        srcPixels,
        reformatTargetModality(target)
    );
    const resampledImage = resampleInt16Grid(srcPixels, srcGrid, dstGrid, {
        patientSrcFromDst: mat4.create(),
        clampToEdge: false,
        backgroundValue: paddingValue,
    });

    // Filled in the output's own frame rather than by extending the
    // sampler; see {@link replicateBoundarySlices} for why.
    replicateBoundarySlices(
        resampledImage.pixels,
        resampledImage.dstGrid,
        output.inferiorSlices,
        output.superiorSlices
    );

    // Reused, not regenerated: the resample does not leave the patient
    // coordinate system. See `DerivedSeriesOptions.preserveFrameOfReferenceUid`.
    const originalForUid: string | undefined =
        exampleDataset.FrameOfReferenceUID;

    // Build DICOM series. The padded fraction travels with the object:
    // the panel warning reaches whoever ran the reformat, this reaches
    // whoever opens the series in a TPS afterwards.
    const newDicoms = await buildDerivedSeriesFromVolume(
        resampledImage.pixels,
        resampledImage.dstGrid,
        exampleDataset,
        {
            preserveFrameOfReferenceUid: originalForUid,
            seriesNumber,
            replicated: {
                inferiorSlices: output.inferiorSlices,
                superiorSlices: output.superiorSlices,
            },
            derivationDescription: reformatDerivationDescription({
                paddedFraction: paddedOfRegion,
                paddingValue,
                gapFilledMm,
                replication:
                    replicatedSlices > 0
                        ? {
                              slices: replicatedSlices,
                              marginMm: extendedMarginMm(
                                  extension,
                                  sliceThicknessMm
                              ),
                              excludedMm: excludedByExtensionMm(
                                  defaultRegionForSlices(
                                      reformatSourceSlices(target)
                                  ).zRangeMm,
                                  region,
                                  sliceThicknessMm,
                                  extension
                              ),
                          }
                        : undefined,
            }),
        }
    );

    // Override series description if user provided one
    const desc = outputDescription.trim();
    if (desc) {
        for (const ds of newDicoms) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            ds.SeriesDescription = desc;
        }
    }

    return { dicomDatasets: newDicoms };
}
